import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Save, Loader2, Upload, X } from "lucide-react";
import { useDirectorStore } from "@/stores/directorStore";
import { directorService } from "@/services/directorService";
import { ROUTES } from "@/constants/routes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

// Validation schema
const createDirectorSchema = z.object({
  name: z
    .string()
    .min(1, "Tên đạo diễn không được để trống")
    .max(255, "Tên không được quá 255 ký tự"),
  url: z
    .string()
    .min(1, "URL slug không được để trống")
    .max(255, "URL không được quá 255 ký tự")
    .regex(
      /^[a-z0-9-]+$/,
      "URL chỉ được chứa chữ thường, số và dấu gạch ngang"
    ),
  description: z.string().optional(),
  birthday: z
    .string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const date = new Date(val);
      return date <= new Date();
    }, "Ngày sinh không được ở tương lai"),
  countryName: z.string().optional(),
  height: z
    .union([z.number().positive("Chiều cao phải là số dương"), z.string()])
    .optional(),
  seoTitle: z.string().max(70, "SEO Title không được quá 70 ký tự").optional(),
  metaDescription: z
    .string()
    .max(320, "Meta Description không được quá 320 ký tự")
    .optional(),
});

type CreateDirectorFormData = z.infer<typeof createDirectorSchema>;

interface Country {
  _id: string;
  name: string;
}

export default function CreateDirectorPage() {
  const navigate = useNavigate();
  const { createDirectorWithImage, isCreating } = useDirectorStore();

  // Countries state
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // Drag & drop states
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateDirectorFormData>({
    resolver: zodResolver(createDirectorSchema),
    defaultValues: {
      name: "",
      url: "",
      description: "",
      birthday: "",
      countryName: "",
      height: "",
      seoTitle: "",
      metaDescription: "",
    },
  });

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response: any = await directorService.getCountries();
        if (response.ok && response.data?.data?.data) {
          setCountries(response.data.data.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setIsLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Auto-generate slug from name
  const nameValue = watch("name");
  useEffect(() => {
    if (nameValue && nameValue.trim()) {
      const slug = nameValue
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "d")
        .replace(/Đ/g, "d")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "")
        .trim();
      setValue("url", slug);
    }
  }, [nameValue, setValue]);

  // Drag & drop handlers
  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file hình ảnh");
      return;
    }
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeImage = () => {
    setPreviewImage("");
    setSelectedFile(null);
  };

  const onSubmit = async (data: CreateDirectorFormData) => {
    const directorData = {
      name: data.name,
      url: data.url,
      description: data.description || undefined,
      birthday: data.birthday || undefined,
      countryName: data.countryName || undefined,
      height: data.height ? Number(data.height) : undefined,
      seoTitle: data.seoTitle || undefined,
      metaDescription: data.metaDescription || undefined,
    };

    const success = await createDirectorWithImage(
      directorData,
      selectedFile || undefined
    );
    if (success) {
      navigate(ROUTES.DIRECTORS);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(ROUTES.DIRECTORS)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Quay lại
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                Thêm đạo diễn mới
              </h1>
              <p className="text-gray-500 mt-1">
                Tạo đạo diễn với đầy đủ thông tin
              </p>
            </div>
          </div>
          <Button
            type="submit"
            form="create-director-form"
            disabled={isCreating}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Đang lưu...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Lưu đạo diễn
              </>
            )}
          </Button>
        </div>

        <form id="create-director-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Thông tin cơ bản</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Tên đạo diễn *</Label>
                      <Input
                        id="name"
                        {...register("name")}
                        placeholder="Nhập tên đạo diễn..."
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="url">URL Slug *</Label>
                      <Input
                        id="url"
                        {...register("url")}
                        placeholder="ten-dao-dien"
                      />
                      {errors.url && (
                        <p className="text-red-500 text-sm">
                          {errors.url.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Mô tả / Tiểu sử</Label>
                    <Textarea
                      id="description"
                      {...register("description")}
                      placeholder="Mô tả về đạo diễn..."
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="birthday">Ngày sinh</Label>
                      <Input
                        id="birthday"
                        type="date"
                        {...register("birthday")}
                      />
                      {errors.birthday && (
                        <p className="text-red-500 text-sm">
                          {errors.birthday.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="countryName">Quốc gia</Label>
                      <Select
                        value={watch("countryName") || ""}
                        onValueChange={(value) =>
                          setValue("countryName", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingCountries
                                ? "Đang tải..."
                                : "Chọn quốc gia"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country._id} value={country.name}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="height">Chiều cao (cm)</Label>
                      <Input
                        id="height"
                        type="number"
                        {...register("height")}
                        placeholder="170"
                      />
                      {errors.height && (
                        <p className="text-red-500 text-sm">
                          {errors.height.message}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SEO Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Thông tin SEO</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="seoTitle">
                      SEO Title
                      <span className="text-gray-400 text-sm ml-2">
                        ({watch("seoTitle")?.length || 0}/70)
                      </span>
                    </Label>
                    <Input
                      id="seoTitle"
                      {...register("seoTitle")}
                      placeholder={`Đạo diễn ${
                        watch("name") || ""
                      } - GhienPhim`}
                      maxLength={70}
                    />
                    {errors.seoTitle && (
                      <p className="text-red-500 text-sm">
                        {errors.seoTitle.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="metaDescription">
                      Meta Description
                      <span className="text-gray-400 text-sm ml-2">
                        ({watch("metaDescription")?.length || 0}/320)
                      </span>
                    </Label>
                    <Textarea
                      id="metaDescription"
                      {...register("metaDescription")}
                      placeholder="Mô tả ngắn gọn về đạo diễn cho SEO..."
                      maxLength={320}
                      rows={3}
                    />
                    {errors.metaDescription && (
                      <p className="text-red-500 text-sm">
                        {errors.metaDescription.message}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Image & Preview */}
            <div className="space-y-6">
              {/* Image Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Hình ảnh</CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    ref={dropRef}
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 cursor-pointer ${
                      isDragging
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                      document.getElementById("director-image-input")?.click()
                    }
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                      id="director-image-input"
                    />

                    {previewImage ? (
                      <div className="space-y-4">
                        <div className="relative inline-block">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage();
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 truncate max-w-xs mx-auto">
                          {selectedFile?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {selectedFile?.size
                            ? (selectedFile.size / 1024 / 1024).toFixed(2)
                            : 0}{" "}
                          MB
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Kéo thả hình ảnh vào đây
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            hoặc nhấp để chọn file
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          JPG, PNG, GIF (tối đa 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* SEO Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Xem trước Google</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-white border rounded-lg p-4 space-y-1">
                    <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                      {watch("seoTitle") ||
                        `Đạo diễn ${watch("name") || "..."} - GhienPhim`}
                    </div>
                    <div className="text-green-700 text-sm">
                      phimmoihd.it.com/dao-dien/{watch("url") || "..."}
                    </div>
                    <div className="text-gray-600 text-sm line-clamp-2">
                      {watch("metaDescription") ||
                        `Thông tin và danh sách phim của đạo diễn ${
                          watch("name") || "..."
                        }. Xem phim hay tại GhienPhim.`}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
